import { useAuth } from 'module/auth/context/AuthContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import LegalAgreementModal from 'module/workplaces/components/LegalAgreementModal/LegalAgreementModal';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { CircularProgress, FormControl, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

// CSS modules
import './ChooseWorkplace.scss';
import '../AuthForm/style.scss';
import Buttons from 'styles/buttons.module.scss';
import { WorkplaceItem, Workplace } from 'module/workplaces/interface/WorkplacesInterface';
import { CheckUserLegalAgreement } from '../../../../core/API/legalAgreements';
import { GetAllWorkplaces } from '../../../../core/API/workplaces';
import UpdatePassword from '../UpdatePassword/UpdatePassword';
import { apiService } from '../../../../core/services/apiService';
import { UpdateUserRequest } from '../../../../core/API/userSettings';
import { formatWorkplaceUnitNumber } from 'shared/utils/stringUtils';

interface iChooseWorkplace {
	chooseWorkplace: () => void;
}

// Utility function to convert timezone data to moment-timezone compatible format
const convertToMomentTimezone = (timezone: any): string => {
	// First, try to use the timeZones field if it exists and is a valid IANA timezone
	if (timezone?.timeZones) {
		try {
			// Check if it's a valid IANA timezone
			if (moment.tz.zone(timezone.timeZones)) {
				return timezone.timeZones;
			}
		} catch (e) {
			// Continue to next option if timeZones is not valid
		}
	}

	// Try to parse the offset to create a fixed offset timezone
	if (timezone?.offset) {
		try {
			// Parse offset like "UTC-10" or "UTC+5:30"
			const offsetMatch = timezone.offset.match(/UTC([+-]?\d{1,2})(?::(\d{2}))?/);
			console.log('Offset Match:', offsetMatch);
			if (offsetMatch) {
				const hours = parseInt(offsetMatch[1], 10);
				const minutes = offsetMatch[2] ? parseInt(offsetMatch[2], 10) : 0;

				// For Etc/GMT format, the sign is inverted: Etc/GMT+4 means UTC-4
				// So if we have UTC-4, we need Etc/GMT+4
				const invertedHours = -hours;

				// Format as Etc/GMT±H (not ±HHMM)
				if (minutes === 0) {
					if (invertedHours === 0) {
						return 'UTC';
					}
					const sign = invertedHours > 0 ? '+' : '';
					return `Etc/GMT${sign}${invertedHours}`;
				} else {
					// For offsets with minutes, use a different approach
					// Create offset string like +04:30 or -05:00
					const offsetMinutes = hours * 60 + (hours >= 0 ? minutes : -minutes);
					const offsetString = moment().utcOffset(offsetMinutes).format('Z');

					// Try to find a matching IANA timezone for common offsets
					const offsetToTimezone: { [key: string]: string } = {
						'-10:00': 'Pacific/Honolulu',
						'-09:00': 'America/Anchorage',
						'-08:00': 'America/Los_Angeles',
						'-07:00': 'America/Denver',
						'-06:00': 'America/Chicago',
						'-05:00': 'America/New_York',
						'-04:00': 'America/Halifax',
						'-03:00': 'America/Sao_Paulo',
						'+00:00': 'UTC',
						'+01:00': 'Europe/London',
						'+02:00': 'Europe/Paris',
						'+08:00': 'Asia/Shanghai',
						'+09:00': 'Asia/Tokyo',
						'+10:00': 'Australia/Sydney',
						'+11:00': 'Pacific/Noumea',
					};
					console.log(
						'Offset String:',
						offsetString,
						'Mapped Timezone:',
						offsetToTimezone[offsetString]
					);
					return offsetToTimezone[offsetString] || 'UTC';
				}
			}
		} catch (e) {
			// Continue to next option if offset parsing fails
		}
	}

	// Try to extract timezone from label
	if (timezone?.label) {
		try {
			// Common timezone abbreviations to IANA mapping
			const timezoneMap: { [key: string]: string } = {
				HST: 'Pacific/Honolulu',
				AKDT: 'America/Anchorage',
				AKST: 'America/Anchorage',
				PDT: 'America/Los_Angeles',
				PST: 'America/Los_Angeles',
				MDT: 'America/Denver',
				MST: 'America/Denver',
				CDT: 'America/Chicago',
				CST: 'America/Chicago',
				EDT: 'America/New_York',
				EST: 'America/New_York',
				GMT: 'UTC',
				UTC: 'UTC',
			};

			// Check if we can extract a known timezone abbreviation
			for (const [abbr, iana] of Object.entries(timezoneMap)) {
				if (timezone.label.includes(abbr)) {
					return iana;
				}
			}

			// Try to extract city names and match to common IANA timezones
			const cityMap: { [key: string]: string } = {
				Honolulu: 'Pacific/Honolulu',
				Anchorage: 'America/Anchorage',
				'Los Angeles': 'America/Los_Angeles',
				Denver: 'America/Denver',
				Chicago: 'America/Chicago',
				'New York': 'America/New_York',
				London: 'Europe/London',
				Paris: 'Europe/Paris',
				Sydney: 'Australia/Sydney',
				Tokyo: 'Asia/Tokyo',
			};

			for (const [city, iana] of Object.entries(cityMap)) {
				if (timezone.label.toLowerCase().includes(city.toLowerCase())) {
					return iana;
				}
			}
		} catch (e) {
			// Continue to fallback if label parsing fails
		}
	}

	// Fallback to UTC if nothing else works
	return 'UTC';
};

export default function ChooseWorkplace({ chooseWorkplace }: iChooseWorkplace): JSX.Element {
	const { data, setWorkplaceIDHandler } = useWorkplacesContext();
	const [workplace, setWorkplace] = useState('');
	const auth = useAuth();
	const [currentUserId, setCurrentUserId] = useState<string | number>('');
	const [activeIdentifier, setActiveIdentifier] = useState<string | null>(null);
	const [_isLoading, _setIsLoading] = useState(true);
	const [screenState, setScreenState] = useState<'loading' | 'eula' | 'password' | 'workplace'>(
		'loading'
	);
	const [needsPasswordUpdate, setNeedsPasswordUpdate] = useState(false);
	const [_needsEula, setNeedsEula] = useState(false);
	const [workplacesMap, setWorkplacesMap] = useState<Record<string, Workplace>>({});
	const [sharedEmployeeWorkplace, setSharedEmployeeWorkplace] = useState<string | null>(null);

	// Fetch all workplaces data
	useEffect(() => {
		const fetchWorkplaces = async () => {
			try {
				const members = await GetAllWorkplaces();
				if (members.length > 0) {
					const workplaces = members.reduce(
						(acc: Record<string, Workplace>, workplace: Workplace) => {
							acc[workplace.id.toString()] = workplace;
							return acc;
						},
						{}
					);
					setWorkplacesMap(workplaces);
					try {
						localStorage.setItem('workplaces-map', JSON.stringify(workplaces));
					} catch (e) {
						console.error('Error saving workplaces to localStorage:', e);
					}
				}
			} catch (error) {
				console.error('Error fetching workplaces:', error);
			}
		};

		fetchWorkplaces();
	}, []);

	// Check user state and determine the flow
	useEffect(() => {
		let mounted = true;

		// Add a condition to skip EULA for specific emails
		const skipEulaEmails = ['admin@byod.ai', 'support@byod.ai'];

		const checkUserState = async () => {
			try {
				setScreenState('loading');
				const byodApp = JSON.parse(localStorage.getItem('BYOD-app') || '{}');

				if (!byodApp?.token_key) {
					if (mounted) setScreenState('workplace');
					return;
				}

				const userId = localStorage.getItem('userId') || '';
				if (mounted) setCurrentUserId(userId);
				if (mounted) {
					setSharedEmployeeWorkplace(byodApp?.user?.sharedEmployeeWorkplace || null);
				}

				const userEmail = byodApp?.user?.email?.toLowerCase();
				if (skipEulaEmails.includes(userEmail)) {
					setNeedsEula(false);
					setScreenState('workplace');
					return;
				}

				const res = await CheckUserLegalAgreement();
				if (!mounted) return;

				const lastUsers = res?.data?.last_users_legal_agreement_identifier || null;
				const lastActive = res?.data?.last_active_legal_agreement_identifier || null;

				if (lastActive) {
					setActiveIdentifier(lastActive);
					try {
						localStorage.setItem('last_active_legal_agreement_identifier', lastActive);
					} catch (e) {
						// ignore storage errors
					}
				}

				// Set initial states
				const needsEula = lastActive && lastActive !== lastUsers;
				const needsPassword = byodApp?.user?.userMustChangePasswordOnNextLogin;

				if (mounted) {
					setNeedsEula(needsEula);
					setNeedsPasswordUpdate(needsPassword);

					// Determine which screen to show first
					if (needsEula) {
						setScreenState('eula');
					} else if (needsPassword) {
						setScreenState('password');
					} else {
						setScreenState('workplace');
					}
				}
			} catch (err) {
				// console.log('Error during initial check: ', err);
				if (mounted) {
					setScreenState('workplace'); // Fallback to workplace screen
				}
			}
		};

		checkUserState();

		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Auto-select workplace if user has only one workplace
	useEffect(() => {
		if (screenState === 'workplace' && data?.items && data.items.length === 1) {
			const singleWorkplace = data.items[0];
			const momentTimezone = singleWorkplace.time_zone 
				? convertToMomentTimezone(singleWorkplace.time_zone) 
				: 'UTC';
			
			setWorkplaceIDHandler(
				singleWorkplace.id,
				singleWorkplace.time_zone ? singleWorkplace.time_zone.label : null,
				momentTimezone
			);

			// Store the workplace postcode in localStorage
			const workplaceDetails = workplacesMap[singleWorkplace.id.toString()];
			if (workplaceDetails?.postcode) {
				localStorage.setItem('workplace-postcode', workplaceDetails.postcode);
			}

			// Save roles
			auth.setRolesToLocal([]);

			// Automatically proceed to dashboard
			chooseWorkplace();
		}
	}, [screenState, data?.items, workplacesMap, setWorkplaceIDHandler, auth, chooseWorkplace]);

	const setWorkplaceHandler = async (e: any) => {
		setWorkplace(e.target.value);
		data?.items.forEach((value) => {
			if (value.id === +e.target.value) {
				// Convert timezone data to moment-timezone compatible format
				const momentTimezone = value.time_zone ? convertToMomentTimezone(value.time_zone) : 'UTC';
				console.log('Selected Workplace Timezone:', momentTimezone, value);
				setWorkplaceIDHandler(
					+e.target.value,
					value.time_zone ? value.time_zone.label : null,
					momentTimezone
				);
				// Manager role is now handled by security role permissions
				// Store the workplace postcode in localStorage
				const workplaceDetails = workplacesMap[e.target.value];
				if (workplaceDetails?.postcode) {
					localStorage.setItem('workplace-postcode', workplaceDetails.postcode);
				}
				// if (value.manager) {
				// } else {
				// Employee role is now handled by security role permissions
				// }
			}

			// save roles
			if (value.id === e.target.value) {
				auth.setRolesToLocal([]);
			}
		});
	};

	const submitButtonHandler = () => {
		if (workplace) {
			chooseWorkplace();
		}
	};

	const getWorkplaceOptionLabel = (workplaceItem: WorkplaceItem): string => {
		const isHomeWorkplace =
			Boolean(sharedEmployeeWorkplace) && workplaceItem['@id'] !== sharedEmployeeWorkplace;
		const displayName = workplaceItem.workplace_unit_number
			? `${formatWorkplaceUnitNumber(workplaceItem.workplace_unit_number)} - ${workplaceItem.name}`
			: workplaceItem.name;
		return isHomeWorkplace ? `${displayName} (Home)` : displayName;
	};

	const handleWorkplaceSelectChange = (event: SelectChangeEvent<string>) => {
		setWorkplaceHandler({ target: { value: event.target.value } });
	};

	const logoutHandler = () => {
		// If auth provides a logout method use it, otherwise clear localStorage and navigate to login
		if (auth && typeof (auth as any).logout === 'function') {
			(auth as any).logout();
		} else {
			try {
				localStorage.clear();
				// fallback: reload to show login screen
				window.location.reload();
			} catch (e) {
				console.error('Logout failed', e);
			}
		}
	};

	const handlePasswordUpdate = async (newPassword: string) => {
		const byodApp = JSON.parse(localStorage.getItem('BYOD-app') || '{}');
		const user = byodApp.user;
		if (!user) throw new Error('No user data found');

		// Get the tenant ID
		const tenant = localStorage.getItem('BYOD-organizationId');
		if (!tenant) {
			throw new Error('Organization ID not found');
		}

		// Update password using security settings endpoint with lowercase email (with null check)
		await apiService.put('/users/security-settings/password', {
			email: user.email ? user.email.toLowerCase() : '',
			tenant,
			password: newPassword,
		});

		// Update user details to set userMustChangePasswordOnNextLogin to false
		const updateUserPayload: UpdateUserRequest = {
			id: user.id,
			firstName: user.firstName,
			lastName: user.lastName,
			middleName: user.middleName || '',
			email: user.email ? user.email.toLowerCase() : '',
			phone: user.phone || '',
			gender: user.gender || '',
			maritalStatus: user.maritalStatus || '',
			addressLine1: user.addressLine1 || '',
			addressLine2: user.addressLine2 || '',
			city: user.city || '',
			state: user.state || '',
			postcode: user.postcode || '',
			country: user.country || '',
			startDate: user.startDate || null,
			hierarchy: user.hierarchy ? user.hierarchy?.['@id'] || user.hierarchy : null,
			securityRole: user.securityRole ? user.securityRole?.['@id'] || user.securityRole : null,
			active: user.active,
			separation: user.separation,
			separationDate: user.separationDate || null,
			userMustChangePasswordOnNextLogin: false,
		};

		const response = await apiService.put(`/users/${user.id}`, updateUserPayload);
		if (!response.status || response.status >= 300)
			throw new Error('Failed to update user settings');

		// Update local storage to reflect the changes
		byodApp.user = {
			...user,
			userMustChangePasswordOnNextLogin: false,
		};
		localStorage.setItem('BYOD-app', JSON.stringify(byodApp));
	};

	// console.log("data items", data.items)

	const onEulaComplete = () => {
		localStorage.setItem('last_users_legal_agreement_identifier', activeIdentifier);
		setNeedsEula(false);
		if (needsPasswordUpdate) {
			setScreenState('password');
		} else {
			setScreenState('workplace');
		}
	};

	const onPasswordUpdateSuccess = async (newPassword: string) => {
		try {
			await handlePasswordUpdate(newPassword);
			setNeedsPasswordUpdate(false);
			setScreenState('workplace');
		} catch (error) {
			console.error('Error updating password:', error);
			// Let the error propagate to UpdatePassword component for handling
			throw error;
		}
	};

	const renderContent = () => {
		switch (screenState) {
			case 'loading':
				return (
					<div className="chooseWorkplaceLoading" role="status">
						<CircularProgress color="inherit" size={28} />
						<div>Loading account information...</div>
					</div>
				);

			case 'eula':
				return (
					<LegalAgreementModal
						open={true}
						onClose={onEulaComplete}
						userId={currentUserId}
						activeIdentifier={activeIdentifier}
					/>
				);

			case 'password':
				return (
					<UpdatePassword
						open={true}
						onCancel={() => setScreenState('workplace')}
						onSubmit={onPasswordUpdateSuccess}
					/>
				);

			case 'workplace': {
				// Sort workplaces by unit number
				const sortedWorkplaces = data?.items ? [...data.items].sort((a, b) => {
					const unitA = a.workplace_unit_number || '';
					const unitB = b.workplace_unit_number || '';
					return unitA.localeCompare(unitB, undefined, { numeric: true });
				}) : [];

				return (
					<div className="chooseWorkplace">
						<h3 className="chooseWorkplaceTitle">Choose workplace</h3>
						<div className="FormBox chooseWorkplaceForm">
							<div className="formSelectBox">
								<label className="chooseWorkplaceFieldLabel" htmlFor="select">
									Workplace
								</label>
								<FormControl fullWidth className="chooseWorkplaceSelectControl">
									<Select
										id="select"
										name="select"
										value={workplace}
										onChange={handleWorkplaceSelectChange}
										displayEmpty
										renderValue={(selected) => {
											if (!selected) {
												return (
													<span className="chooseWorkplacePlaceholder">
														Choose workplace
													</span>
												);
											}
											const workplaceItem = sortedWorkplaces.find(
												(item) => String(item.id) === selected
											);
											return workplaceItem
												? getWorkplaceOptionLabel(workplaceItem)
												: selected;
										}}
									>
										{sortedWorkplaces?.map((workplaceItem: WorkplaceItem) => (
											<MenuItem value={String(workplaceItem.id)} key={workplaceItem.id}>
												{getWorkplaceOptionLabel(workplaceItem)}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								{sharedEmployeeWorkplace && (
									<div className="workplaceHint">
										Entries marked with {'"(Home)"'} are your home workplaces.
									</div>
								)}
							</div>
							<div className="chooseWorkplaceActions">
								<button
									className={Buttons.btnPrimary}
									type="button"
									onClick={submitButtonHandler}
									disabled={!workplace}
									style={{
										backgroundColor: !workplace ? 'gray' : '#446388',
									}}
								>
									Continue
								</button>
								<a
									href="#"
									className="logoutLink"
									onClick={(e) => {
										e.preventDefault();
										logoutHandler();
									}}
								>
									Logout
								</a>
							</div>
						</div>
					</div>
				);
			}
		}
	};

	// return (
	// 	<div className="FormBox" style={{ height: '330px' }}>
	// 		{renderContent()}
	// 	</div>
	// );
	return renderContent();
}
